(function (global) {
  'use strict';

  var OUTPUT_WIDTH = 1000;
  var OUTPUT_HEIGHT = 1000;
  var FRONT_ART_TOP_INSET = 6;
  var NUMBER_OPTICAL_OFFSET = 0.05;
  var FONT_FAMILY = 'RockEm Solo Jersey';
  var jsonPromiseCache = new Map();

  var placements = {
    frontBlank: { x: 140, y: 52 },
    backBlank: { x: 601, y: 66 },
    frontNative: { x: 465, y: 64 },
    backMask: { x: 601, y: 66 }
  };

  function makeCanvas(width, height) {
    var canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var image = new Image();
      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      image.onload = function () {
        resolve(image);
      };
      image.onerror = function () {
        reject(new Error('Unable to load preview image: ' + url));
      };
      image.src = url;
    });
  }

  function fetchJson(url) {
    if (jsonPromiseCache.has(url)) {
      return jsonPromiseCache.get(url);
    }

    var request = fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) {
        return response.json().catch(function () {
          return {};
        }).then(function (body) {
          throw new Error(body.error || 'Preview request failed.');
        });
      }
      return response.json();
    });

    jsonPromiseCache.set(url, request);
    return request.catch(function (error) {
      jsonPromiseCache.delete(url);
      throw error;
    });
  }

  function resolveUrl(value, base) {
    return new URL(value, new URL(base, global.location.origin)).toString();
  }

  function loadFont(url, family) {
    if (!global.FontFace || !document.fonts) {
      return Promise.resolve();
    }

    var face = new FontFace(family || FONT_FAMILY, 'url("' + url + '")');
    return face.load().then(function (loaded) {
      document.fonts.add(loaded);
    });
  }

  function solveTriangleTransform(source, target) {
    var s0 = source[0];
    var s1 = source[1];
    var s2 = source[2];
    var t0 = target[0];
    var t1 = target[1];
    var t2 = target[2];
    var determinant =
      s0.x * (s1.y - s2.y) +
      s1.x * (s2.y - s0.y) +
      s2.x * (s0.y - s1.y);

    if (Math.abs(determinant) < 0.00000001) {
      return null;
    }

    function solve(v0, v1, v2) {
      return {
        x:
          (v0 * (s1.y - s2.y) +
            v1 * (s2.y - s0.y) +
            v2 * (s0.y - s1.y)) /
          determinant,
        y:
          (v0 * (s2.x - s1.x) +
            v1 * (s0.x - s2.x) +
            v2 * (s1.x - s0.x)) /
          determinant,
        offset:
          (v0 * (s1.x * s2.y - s2.x * s1.y) +
            v1 * (s2.x * s0.y - s0.x * s2.y) +
            v2 * (s0.x * s1.y - s1.x * s0.y)) /
          determinant
      };
    }

    var x = solve(t0.x, t1.x, t2.x);
    var y = solve(t0.y, t1.y, t2.y);
    return {
      a: x.x,
      b: y.x,
      c: x.y,
      d: y.y,
      e: x.offset,
      f: y.offset
    };
  }

  function expandTriangle(triangle, amount) {
    var padding = amount === undefined ? 0.75 : amount;
    var center = triangle.reduce(
      function (result, point) {
        result.x += point.x / triangle.length;
        result.y += point.y / triangle.length;
        return result;
      },
      { x: 0, y: 0 }
    );

    return triangle.map(function (point) {
      var deltaX = point.x - center.x;
      var deltaY = point.y - center.y;
      var distance = Math.hypot(deltaX, deltaY) || 1;
      var scale = (distance + padding) / distance;
      return {
        x: center.x + deltaX * scale,
        y: center.y + deltaY * scale
      };
    });
  }

  function drawTriangle(ctx, image, source, target, crop) {
    var transform = solveTriangleTransform(source, target);
    if (!transform) {
      return;
    }

    var clipTarget = expandTriangle(target);
    var padding = Math.max(
      1,
      Math.ceil(Math.max(crop.width, crop.height) * 0.2)
    );
    var sourceX = Math.max(0, crop.x - padding);
    var sourceY = Math.max(0, crop.y - padding);
    var sourceRight = Math.min(
      image.width,
      crop.x + crop.width + padding
    );
    var sourceBottom = Math.min(
      image.height,
      crop.y + crop.height + padding
    );
    var sourceWidth = sourceRight - sourceX;
    var sourceHeight = sourceBottom - sourceY;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(clipTarget[0].x, clipTarget[0].y);
    ctx.lineTo(clipTarget[1].x, clipTarget[1].y);
    ctx.lineTo(clipTarget[2].x, clipTarget[2].y);
    ctx.closePath();
    ctx.clip();
    ctx.setTransform(
      transform.a,
      transform.b,
      transform.c,
      transform.d,
      transform.e,
      transform.f
    );
    ctx.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      sourceX - crop.x,
      sourceY - crop.y,
      sourceWidth,
      sourceHeight
    );
    ctx.restore();
  }

  function renderMesh(source, mesh, outputWidth, outputHeight) {
    var canvas = makeCanvas(
      outputWidth || OUTPUT_WIDTH,
      outputHeight || OUTPUT_HEIGHT
    );
    var ctx = canvas.getContext('2d');
    var stride = mesh.columns + 1;
    var cellWidth = mesh.width / mesh.columns;
    var cellHeight = mesh.height / mesh.rows;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    for (var row = 0; row < mesh.rows; row += 1) {
      for (var column = 0; column < mesh.columns; column += 1) {
        var topLeft = row * stride + column;
        var topRight = topLeft + 1;
        var bottomLeft = topLeft + stride;
        var bottomRight = bottomLeft + 1;
        var crop = {
          x: mesh.rootPositions[topLeft].x,
          y: mesh.rootPositions[topLeft].y,
          width: cellWidth,
          height: cellHeight
        };

        drawTriangle(
          ctx,
          source,
          [
            { x: 0, y: 0 },
            { x: cellWidth, y: 0 },
            { x: 0, y: cellHeight }
          ],
          [
            mesh.positions[topLeft],
            mesh.positions[topRight],
            mesh.positions[bottomLeft]
          ],
          crop
        );
        drawTriangle(
          ctx,
          source,
          [
            { x: cellWidth, y: cellHeight },
            { x: 0, y: cellHeight },
            { x: cellWidth, y: 0 }
          ],
          [
            mesh.positions[bottomRight],
            mesh.positions[bottomLeft],
            mesh.positions[topRight]
          ],
          crop
        );
      }
    }

    return canvas;
  }

  function translateCanvas(source, x, y) {
    var canvas = makeCanvas(OUTPUT_WIDTH, OUTPUT_HEIGHT);
    canvas.getContext('2d').drawImage(source, x, y);
    return canvas;
  }

  function preparePanel(tile, mesh) {
    var canvas = makeCanvas(mesh.width, mesh.height);
    var ctx = canvas.getContext('2d');
    var panelWidth = Math.floor(tile.width / 2);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      tile,
      0,
      0,
      panelWidth,
      tile.height,
      0,
      0,
      mesh.width,
      mesh.height
    );
    return canvas;
  }

  function prepareStandardFrontSource(tile) {
    var scaledTexture = makeCanvas(720, 1188);
    var scaledContext = scaledTexture.getContext('2d');
    scaledContext.imageSmoothingEnabled = true;
    scaledContext.imageSmoothingQuality = 'high';
    scaledContext.drawImage(
      tile,
      0,
      0,
      tile.width,
      tile.height,
      54,
      18,
      612,
      1094.4
    );

    var source = makeCanvas(297, 974);
    var sourceContext = source.getContext('2d');
    sourceContext.imageSmoothingEnabled = true;
    sourceContext.imageSmoothingQuality = 'high';
    sourceContext.drawImage(scaledTexture, -295, -1, 590, 975);
    return source;
  }

  function createLuminanceMask(image) {
    var canvas = makeCanvas(image.width, image.height);
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(image, 0, 0);
    var pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
    var usesTransparency = false;

    for (var alphaIndex = 3; alphaIndex < pixels.data.length; alphaIndex += 4) {
      if (pixels.data[alphaIndex] !== 255) {
        usesTransparency = true;
        break;
      }
    }

    if (!usesTransparency) {
      for (var index = 0; index < pixels.data.length; index += 4) {
        pixels.data[index + 3] = pixels.data[index];
      }
      ctx.putImageData(pixels, 0, 0);
    }

    return canvas;
  }

  function clipWarpToMask(warp, mask, placement) {
    var canvas = makeCanvas(OUTPUT_WIDTH, OUTPUT_HEIGHT);
    var ctx = canvas.getContext('2d');
    ctx.drawImage(warp, 0, 0);
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(mask, placement.x, placement.y);
    ctx.globalCompositeOperation = 'source-over';
    return canvas;
  }

  function cropPanel(image, area) {
    var canvas = makeCanvas(area.width, area.height);
    var ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      image,
      area.x,
      area.y,
      area.width,
      area.height,
      0,
      0,
      area.width,
      area.height
    );
    return canvas;
  }

  function getSoloPanelArea(image, area) {
    var imageWidth = image.naturalWidth || image.width;
    var availableWidth = Math.max(1, imageWidth - area.x);
    var panelWidth = area.width;

    // Solo art is a single 1725px-wide panel. The template still carries the
    // legacy 2550px doubled print width, so crop one half before duplicating it
    // into the print tile—matching createArtPreview's backend pipeline.
    if (panelWidth > availableWidth) {
      panelWidth = Math.floor(panelWidth / 2);
    }

    return {
      x: area.x,
      y: area.y,
      width: Math.min(panelWidth, availableWidth),
      height: area.height
    };
  }

  function prepareSoloFrontPanel(image, area) {
    var imageWidth = image.naturalWidth || image.width;
    var imageHeight = image.naturalHeight || image.height;
    var availableHeight = Math.max(1, imageHeight - area.y);
    var panelWidth = area.width;

    // The solo front is authored as one complete 1725px-wide canvas. The
    // production mock pipeline preserves that whole canvas and resizes it to
    // one 1275px L/XL print panel before doubling the panel. Cropping at x=225
    // first makes the artwork too large and shifts it toward the sock edge.
    if (panelWidth > imageWidth) {
      panelWidth = Math.floor(panelWidth / 2);
    }

    var canvas = makeCanvas(panelWidth, area.height);
    var ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      image,
      0,
      area.y,
      imageWidth,
      Math.min(area.height, availableHeight),
      0,
      0,
      panelWidth,
      area.height
    );
    return canvas;
  }

  function makeTile(panel) {
    var canvas = makeCanvas(panel.width * 2, panel.height);
    var ctx = canvas.getContext('2d');
    ctx.drawImage(panel, 0, 0);
    ctx.drawImage(panel, panel.width, 0);
    return canvas;
  }

  function createTextCanvas(value, fontSize, color, family, style) {
    var probe = makeCanvas(1, 1);
    var probeContext = probe.getContext('2d');
    probeContext.font = fontSize + 'px "' + (family || FONT_FAMILY) + '"';
    var metrics = probeContext.measureText(value);
    // Canvas reports the left ink edge relative to the text origin. Shift
    // that edge to zero before rotating so names start at the slot's top.
    var left = metrics.actualBoundingBoxLeft;
    var right = metrics.actualBoundingBoxRight;
    var ascent = metrics.actualBoundingBoxAscent;
    var descent = metrics.actualBoundingBoxDescent;
    var tracking = style ? style.trackingEm * fontSize : 0;
    var stroke = style ? style.strokeEm * fontSize : 0;
    var positions = [];
    if (style) {
      // Measuring shaped prefixes moves a pair's kerning onto the next glyph.
      // Independent glyph advances keep all letter gaps clear, including TAR.
      var advance = 0, inkLeft = Infinity, inkRight = -Infinity;
      ascent = descent = 0;
      for (var glyphIndex = 0; glyphIndex < value.length; glyphIndex++) {
        var glyphMetrics = probeContext.measureText(value[glyphIndex]);
        positions.push(advance);
        inkLeft = Math.min(inkLeft, advance - glyphMetrics.actualBoundingBoxLeft);
        inkRight = Math.max(inkRight, advance + glyphMetrics.actualBoundingBoxRight);
        ascent = Math.max(ascent, glyphMetrics.actualBoundingBoxAscent);
        descent = Math.max(descent, glyphMetrics.actualBoundingBoxDescent);
        advance += glyphMetrics.width + tracking;
      }
      left = -inkLeft;
      right = inkRight;
    }
    var width = Math.max(1, Math.ceil(left + right + stroke));
    var height = Math.max(1, Math.ceil(ascent + descent + stroke));
    var canvas = makeCanvas(width, height);
    var ctx = canvas.getContext('2d');
    ctx.font = fontSize + 'px "' + (family || FONT_FAMILY) + '"';
    ctx.fillStyle = color;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    if (style) {
      ctx.strokeStyle = color;
      ctx.lineWidth = stroke;
      ctx.lineJoin = 'round';
      for (var index = 0; index < value.length; index++) {
        var x = left + stroke / 2 + positions[index];
        if (stroke) ctx.strokeText(value[index], x, ascent + stroke / 2);
        ctx.fillText(value[index], x, ascent + stroke / 2);
      }
    } else {
      ctx.fillText(value, left, ascent);
    }
    return canvas;
  }

  function rotateCanvas(source, degrees) {
    if (!degrees) {
      return source;
    }

    var normalized = ((degrees % 360) + 360) % 360;
    var swapsSides = normalized === 90 || normalized === 270;
    var canvas = makeCanvas(
      swapsSides ? source.height : source.width,
      swapsSides ? source.width : source.height
    );
    var ctx = canvas.getContext('2d');
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((normalized * Math.PI) / 180);
    ctx.drawImage(source, -source.width / 2, -source.height / 2);
    return canvas;
  }

  function trimCanvasToInk(source) {
    var pixels = source.getContext('2d').getImageData(0, 0, source.width, source.height).data;
    var left = source.width, top = source.height, right = -1, bottom = -1;
    for (var y = 0; y < source.height; y++) {
      for (var x = 0; x < source.width; x++) {
        if (!pixels[(y * source.width + x) * 4 + 3]) continue;
        left = Math.min(left, x);
        top = Math.min(top, y);
        right = Math.max(right, x);
        bottom = Math.max(bottom, y);
      }
    }
    if (right < left) return source;
    return cropPanel(source, { x: left, y: top, width: right - left + 1, height: bottom - top + 1 });
  }

  function drawProperty(panel, slot, value, color, family, fillNameWidth, nameFit) {
    if (!value) {
      return;
    }

    var isName = slot.field === 'Name';
    var fitName = isName && fillNameWidth;
    var style = fitName && nameFit;
    if (style) {
      // Keep the measured ink width; use the table's full height and top anchor.
      slot = Object.assign({}, slot, {
        x: slot.x + nameFit.x * slot.width,
        y: slot.y,
        width: nameFit.width * slot.width,
        height: slot.height
      });
    }
    var fontSize = style ? style.fontSize : Math.round(
      (isName ? slot.width : slot.height) * 1.3
    );
    var rendered = rotateCanvas(
      createTextCanvas(value, fontSize, color, family, style),
      Number(slot.rotation) || 0
    );
    if (fitName) rendered = trimCanvasToInk(rendered);
    // Version 2 fills the font frame's ink width, including short names.
    // At the height limit, compress only the vertical direction.
    var scale = fitName ? slot.width / rendered.width : Math.min(1, slot.width / rendered.width);
    var width = fitName ? slot.width : Math.max(1, Math.round(rendered.width * scale));
    var proportionalHeight = Math.max(
      1,
      Math.round(rendered.height * scale)
    );
    var height = Math.min(proportionalHeight, slot.height);
    // Names intentionally begin at the start of their vertical name track.
    // Numbers, however, belong in the center of the number slot. `rendered`
    // is cropped to the glyph's actual ink bounds. The back-sock warp makes a
    // geometric center read slightly right-heavy, so apply a small leftward
    // optical correction after centering narrow values such as "1" and "11".
    var left = isName
      ? Math.round(slot.x)
      : Math.round(
          slot.x +
          (slot.width - width) / 2 -
          slot.width * NUMBER_OPTICAL_OFFSET
        );
    var top = isName
      ? Math.round(slot.y)
      : Math.round(slot.y + (slot.height - height) / 2);

    panel
      .getContext('2d')
      .drawImage(rendered, 0, 0, rendered.width, rendered.height, left, top, width, height);
  }

  function drawCustomization(panel, customization, values, nameTopInset) {
    var slots =
      values.track === 'name-only' &&
      customization.propertySlotsBackNameOnly.length
        ? customization.propertySlotsBackNameOnly
        : customization.propertySlotsBack;
    var color = customization.textColor || '#000000';

    slots.forEach(function (slot) {
      var value = slot.field === 'Name' ? values.name : values.number;
      if (slot.field === 'Name' && typeof nameTopInset === 'number') {
        slot = Object.assign({}, slot, { y: nameTopInset });
      }
      drawProperty(panel, slot, value, color, customization.nameFontFamily, customization.layoutVersion === 'version-2', customization.nameFit);
    });
  }

  function drawVectorNumber(panel, slot, value, digits) {
    var number = String(value || '');
    if (!number) return;
    if (!/^\d{1,2}$/.test(number)) throw new Error('Enter one or two digits.');
    var glyphs = number.split('').map(function (digit, index) {
      var position = number.length === 1 ? (digits.singleDigitPosition || 'base') : index === 0 ? 'leading' : 'trailing';
      var kind = number.length === 1 ? 'single' : index === 0 ? 'leading' : 'trailing';
      var rules = digits.glyphRules;
      if (rules && rules[kind] && rules[kind][digit]) position = rules[kind][digit];
      if (index < number.length - 1 && rules && rules.before && rules.before[digit] && rules.before[digit][number[index + 1]]) {
        position = rules.before[digit][number[index + 1]];
      }
      if (index > 0 && rules && rules.after && rules.after[digit] && rules.after[digit][number[index - 1]]) {
        position = rules.after[digit][number[index - 1]];
      }
      var glyph = digits.positionalDigits && digits.positionalDigits[position] && digits.positionalDigits[position][digit] || digits[digit];
      if (!glyph) throw new Error('This team is missing SVG digit ' + digit + '.');
      return glyph;
    });
    var ratios = glyphs.map(function (glyph) {
      // SVG viewBoxes can be fractional; naturalWidth/Height round them.
      return (glyph.jerseyGlyphWidth || glyph.naturalWidth || glyph.width) /
        (glyph.jerseyGlyphHeight || glyph.naturalHeight || glyph.height);
    });
    var fixedGap = typeof slot.gap === 'number' ? slot.gap : null;
    var gapRatio = fixedGap === null ? 0.035 : 0;
    var ratio = ratios.reduce(function (sum, width) { return sum + width; }, 0) + gapRatio * (glyphs.length - 1);
    var gapWidth = fixedGap === null ? 0 : fixedGap * (glyphs.length - 1);
    var height = Math.min(slot.height, (slot.width - gapWidth) / ratio);
    var left = slot.x + (slot.width - ratio * height - gapWidth) / 2;
    var top = slot.y + (slot.height - height) / 2;
    glyphs.forEach(function (glyph, index) {
      var width = ratios[index] * height;
      panel.getContext('2d').drawImage(glyph, left, top, width, height);
      left += width + (fixedGap === null ? gapRatio * height : fixedGap);
    });
  }

  function loadNumberSetImages(numberSet, metadataUrl) {
    var jobs = Object.keys(numberSet.digits).map(function (digit) {
      return ['base', digit, numberSet.digits[digit]];
    });
    Object.keys(numberSet.positionalDigits || {}).forEach(function (position) {
      Object.keys(numberSet.positionalDigits[position]).forEach(function (digit) {
        jobs.push([position, digit, numberSet.positionalDigits[position][digit]]);
      });
    });
    var imageCache = new Map();
    return Promise.all(jobs.map(function (job) {
      var url = resolveUrl(job[2].url, metadataUrl);
      if (!imageCache.has(url)) imageCache.set(url, loadImage(url));
      return imageCache.get(url).then(function (image) {
        image.jerseyGlyphWidth = job[2].width;
        image.jerseyGlyphHeight = job[2].height;
        return [job[0], job[1], image];
      });
    })).then(function (loaded) {
      var digits = {};
      loaded.forEach(function (job) {
        if (job[0] === 'base') digits[job[1]] = job[2];
        else {
          if (!digits.positionalDigits) digits.positionalDigits = {};
          if (!digits.positionalDigits[job[0]]) digits.positionalDigits[job[0]] = {};
          digits.positionalDigits[job[0]][job[1]] = job[2];
        }
      });
      if (numberSet.positionalDigits) digits.singleDigitPosition = numberSet.singleDigitPosition;
      if (numberSet.glyphRules) digits.glyphRules = numberSet.glyphRules;
      return digits;
    });
  }

  function applyNflPlacements(product) {
    if (product.renderer.id !== 'nfl-custom-jersey-2026') return product;
    // The table defines the fitting boxes. Fronts use original-art coordinates;
    // back names use resized/cropped panel coordinates.
    var customization = Object.assign({}, product.customization);
    var names = customization.propertySlotsBackNameOnly || [];
    var nameSlot = names.find(function (slot) { return slot.field === 'Name'; }) ||
      (customization.propertySlotsBack || []).find(function (slot) { return slot.field === 'Name'; });
    if (!nameSlot) return product;
    customization.nameTopInset = nameSlot.y;
    return Object.assign({}, product, { customization: customization });
  }

  function personalizeNflFront(source, customization, number, digits) {
    var front = makeCanvas(source.width, source.height);
    front.getContext('2d').drawImage(source, 0, 0);
    var slot = customization.frontNumberArea;
    var fit = customization.numberSet && customization.numberSet.fit;
    if (fit) {
      // Font-specific lettering sits inside the table's unchanged fitting box.
      // Fractions follow the same source-art resize transform for every size.
      slot = {
        x: slot.x + fit.x * slot.width,
        y: slot.y + fit.y * slot.height,
        width: fit.width * slot.width,
        height: fit.height * slot.height,
        gap: fit.gap * slot.width
      };
    }
    var transform = customization.frontNumberTransform;
    if (transform && (transform.sx !== 1 || transform.sy !== 1)) {
      // Personalize in original-art proportions, then use exactly the artwork's
      // resize transform. Independent x/y scaling must affect the digits too.
      var original = { x: 0, y: 0, width: slot.width / transform.sx, height: slot.height / transform.sy, gap: slot.gap / transform.sx };
      var ink = makeCanvas(Math.ceil(original.width), Math.ceil(original.height));
      drawVectorNumber(ink, original, number, digits);
      front.getContext('2d').drawImage(ink, 0, 0, original.width, original.height, slot.x, slot.y, slot.width, slot.height);
    } else {
      drawVectorNumber(front, slot, number, digits);
    }
    // Finish one personalized panel before duplicating it. Cropping preserves
    // logo and number proportions; the legacy Solo front preparation squeezes them.
    return { source: front, panel: cropPanel(front, getSoloPanelArea(front, customization.printArea)) };
  }

  function createFrontLayer(frontTile, resources) {
    var frontSource = prepareStandardFrontSource(frontTile);
    var stageOne = renderMesh(
      frontSource,
      resources.frontStageOneMesh,
      resources.frontStageTwoMesh.width,
      resources.frontStageTwoMesh.height
    );
    var stageTwo = renderMesh(stageOne, resources.frontStageTwoMesh);
    var insetStageTwo = translateCanvas(stageTwo, 0, FRONT_ART_TOP_INSET);
    var nativeWarp = clipWarpToMask(
      insetStageTwo,
      resources.frontMask,
      placements.frontNative
    );
    var insideSock = clipWarpToMask(
      nativeWarp,
      resources.frontSilhouette,
      placements.frontNative
    );
    var frontWarp = translateCanvas(
      insideSock,
      placements.frontBlank.x - placements.frontNative.x,
      placements.frontBlank.y - placements.frontNative.y
    );
    var canvas = makeCanvas(OUTPUT_WIDTH, OUTPUT_HEIGHT);
    var ctx = canvas.getContext('2d');

    ctx.drawImage(frontWarp, 0, 0);
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.drawImage(
      resources.frontBase,
      placements.frontBlank.x,
      placements.frontBlank.y
    );
    ctx.restore();
    ctx.save();
    ctx.globalCompositeOperation = 'multiply';
    ctx.drawImage(
      resources.frontBlank,
      placements.frontBlank.x,
      placements.frontBlank.y
    );
    ctx.restore();
    ctx.drawImage(
      resources.frontGlow,
      placements.frontBlank.x,
      placements.frontBlank.y
    );
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    ctx.drawImage(
      resources.frontToes,
      placements.frontBlank.x,
      placements.frontBlank.y
    );
    ctx.globalAlpha = 0.8;
    ctx.globalCompositeOperation = 'multiply';
    ctx.drawImage(
      resources.frontToes,
      placements.frontBlank.x,
      placements.frontBlank.y
    );
    ctx.restore();
    return canvas;
  }

  function renderMock(frontLayer, backTile, resources) {
    var backSource = preparePanel(backTile, resources.backMesh);
    var backWarp = clipWarpToMask(
      renderMesh(backSource, resources.backMesh),
      resources.backMask,
      placements.backMask
    );
    var canvas = makeCanvas(OUTPUT_WIDTH, OUTPUT_HEIGHT);
    var ctx = canvas.getContext('2d');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
    ctx.drawImage(frontLayer, 0, 0);
    ctx.drawImage(backWarp, 0, 0);
    ctx.save();
    ctx.globalCompositeOperation = 'multiply';
    ctx.drawImage(
      resources.backBlank,
      placements.backBlank.x,
      placements.backBlank.y
    );
    ctx.restore();
    return canvas;
  }

  function copyCanvas(source, target) {
    target.width = source.width;
    target.height = source.height;
    target.getContext('2d').drawImage(source, 0, 0);
  }

  function SoloCustomJerseyPreview(config) {
    this.config = config;
    this.resourcesPromise = null;
    this.product = null;
    this.frontLayer = null;
    this.backPanel = null;
    this.activeSku = '';
    this.loadToken = 0;
    this.productCache = new Map();
    this.frontLayerCache = new Map();
    this.maxProductCacheEntries = Math.max(
      1,
      Number(config.maxProductCacheEntries) || 6
    );
    this.maxFrontLayerCacheEntries = Math.max(
      this.maxProductCacheEntries,
      Number(config.maxFrontLayerCacheEntries) || 15
    );
  }

  SoloCustomJerseyPreview.prototype.loadResources = function () {
    var self = this;
    if (this.resourcesPromise) {
      return this.resourcesPromise;
    }

    if (this.config.mockProfile) {
      if (!global.RockEmNflJerseyPsdMock) return Promise.reject(new Error('The PSD preview renderer is unavailable.'));
      this.resourcesPromise = Promise.all([
        global.RockEmNflJerseyPsdMock.load(resolveUrl(this.config.mockProfile, global.location.href || global.location.origin)),
        loadFont(this.config.font, this.config.fontFamily)
      ]).then(function (values) { return { psdMock: values[0] }; }).catch(function (error) {
        self.resourcesPromise = null;
        throw error;
      });
      return this.resourcesPromise;
    }

    this.resourcesPromise = Promise.all([
      fetchJson(this.config.meshes.frontStageOne),
      fetchJson(this.config.meshes.frontStageTwo),
      fetchJson(this.config.meshes.back),
      loadImage(this.config.layers.frontBase),
      loadImage(this.config.layers.frontBlank),
      loadImage(this.config.layers.frontGlow),
      loadImage(this.config.layers.frontToes),
      loadImage(this.config.layers.backBlank),
      loadImage(this.config.layers.frontMask),
      loadImage(this.config.layers.backMask),
      loadFont(this.config.font, this.config.fontFamily)
    ]).then(function (values) {
      return {
        frontStageOneMesh: values[0],
        frontStageTwoMesh: values[1],
        backMesh: values[2],
        frontBase: values[3],
        frontBlank: values[4],
        frontGlow: values[5],
        frontToes: values[6],
        backBlank: values[7],
        frontMask: createLuminanceMask(values[8]),
        backMask: createLuminanceMask(values[9]),
        frontSilhouette: createLuminanceMask(values[4])
      };
    });

    return this.resourcesPromise.catch(function (error) {
      self.resourcesPromise = null;
      throw error;
    });
  };

  SoloCustomJerseyPreview.prototype.touchProductCache = function (sku, entry) {
    this.productCache.delete(sku);
    this.productCache.set(sku, entry);
  };

  SoloCustomJerseyPreview.prototype.trimProductCache = function () {
    var self = this;
    while (this.productCache.size > this.maxProductCacheEntries) {
      var removableSku = '';
      this.productCache.forEach(function (entry, sku) {
        if (!removableSku && entry.ready && sku !== self.activeSku) {
          removableSku = sku;
        }
      });
      if (!removableSku) return;
      this.productCache.delete(removableSku);
    }
  };

  SoloCustomJerseyPreview.prototype.cacheFrontLayer = function (sku, frontLayer) {
    this.frontLayerCache.delete(sku);
    this.frontLayerCache.set(sku, frontLayer);
    while (this.frontLayerCache.size > this.maxFrontLayerCacheEntries) {
      var oldestSku = this.frontLayerCache.keys().next().value;
      if (oldestSku === this.activeSku) {
        var activeLayer = this.frontLayerCache.get(oldestSku);
        this.frontLayerCache.delete(oldestSku);
        this.frontLayerCache.set(oldestSku, activeLayer);
      } else {
        this.frontLayerCache.delete(oldestSku);
      }
    }
  };

  SoloCustomJerseyPreview.prototype.prepareProduct = function (sku) {
    var self = this;
    var normalizedSku = String(sku || '').trim();
    if (!normalizedSku) {
      return Promise.reject(new Error('A preview SKU is required.'));
    }

    var cached = this.productCache.get(normalizedSku);
    if (cached) {
      this.touchProductCache(normalizedSku, cached);
      return cached.promise;
    }

    var metadataUrl =
      this.config.endpoint +
      (this.config.endpoint.indexOf('?') === -1 ? '?' : '&') +
      'sku=' +
      encodeURIComponent(normalizedSku);
    var entry = { ready: false, promise: null };

    entry.promise = Promise.all([this.loadResources(), fetchJson(metadataUrl)])
      .then(function (values) {
        var resources = values[0];
        var product = applyNflPlacements(values[1]);
        var frontUrl = resolveUrl(product.assets.front.url, metadataUrl);
        var backUrl = resolveUrl(product.assets.back.url, metadataUrl);
        if (product.renderer.id === 'nfl-custom-jersey-2026') {
          return Promise.all([
            resources.psdMock ? global.RockEmNflJerseyPsdMock.loadSource(frontUrl) : loadImage(frontUrl),
            resources.psdMock ? global.RockEmNflJerseyPsdMock.loadSource(backUrl) : loadImage(backUrl),
            loadNumberSetImages(product.customization.numberSet, metadataUrl)
          ]).then(function (images) {
            return {
              sku: normalizedSku,
              product: product,
              frontSource: images[0],
              backSource: images[1],
              digits: images[2],
              frontLayer: null,
              numberFrontLayers: new Map(),
              backPanel: cropPanel(images[1], getSoloPanelArea(images[1], product.customization.printBackArea))
            };
          });
        }
        var cachedFrontLayer = self.frontLayerCache.get(normalizedSku);
        if (cachedFrontLayer) {
          self.cacheFrontLayer(normalizedSku, cachedFrontLayer);
        }
        var frontLayerPromise = cachedFrontLayer
          ? Promise.resolve(cachedFrontLayer)
          : loadImage(frontUrl).then(function (frontImage) {
            var frontPanel = prepareSoloFrontPanel(
              frontImage,
              product.customization.printArea
            );
            var frontLayer = createFrontLayer(makeTile(frontPanel), resources);
            self.cacheFrontLayer(normalizedSku, frontLayer);
            return frontLayer;
          });

        return Promise.all([frontLayerPromise, loadImage(backUrl)]).then(
          function (preparedAssets) {
            return {
              sku: normalizedSku,
              product: product,
              backPanel: cropPanel(
                preparedAssets[1],
                getSoloPanelArea(
                  preparedAssets[1],
                  product.customization.printBackArea
                )
              ),
              frontLayer: preparedAssets[0]
            };
          }
        );
      })
      .then(function (prepared) {
        entry.ready = true;
        self.touchProductCache(normalizedSku, entry);
        self.trimProductCache();
        return prepared;
      })
      .catch(function (error) {
        if (self.productCache.get(normalizedSku) === entry) {
          self.productCache.delete(normalizedSku);
        }
        throw error;
      });

    this.productCache.set(normalizedSku, entry);
    return entry.promise;
  };

  SoloCustomJerseyPreview.prototype.prefetchProduct = function (sku) {
    return this.prepareProduct(sku).catch(function () {
      // Prefetching is opportunistic; loadProduct reports errors if the shopper
      // actually selects the design.
      return null;
    });
  };

  SoloCustomJerseyPreview.prototype.prefetchProducts = function (skus, options) {
    var self = this;
    var uniqueSkus = Array.from(new Set((skus || []).filter(Boolean)));
    var concurrency = Math.max(1, Number(options && options.concurrency) || 2);
    var cursor = 0;

    function worker() {
      var index = cursor++;
      if (index >= uniqueSkus.length) return Promise.resolve();
      return self.prefetchProduct(uniqueSkus[index]).then(worker);
    }

    var workers = [];
    for (var index = 0; index < Math.min(concurrency, uniqueSkus.length); index += 1) {
      workers.push(worker());
    }
    return Promise.all(workers);
  };

  SoloCustomJerseyPreview.prototype.loadProduct = function (sku) {
    var self = this;
    var normalizedSku = String(sku || '').trim();
    var token = ++this.loadToken;
    var cached = this.productCache.get(normalizedSku);

    if (!cached || !cached.ready) {
      this.config.onStatus('loading', 'Building your preview…');
    }

    var preparedProduct = this.prepareProduct(normalizedSku);
    if (typeof this.config.onMetadata === 'function') {
      var metadataUrl = this.config.endpoint +
        (this.config.endpoint.indexOf('?') === -1 ? '?' : '&') +
        'sku=' + encodeURIComponent(normalizedSku);
      fetchJson(metadataUrl).then(function (product) {
        if (token === self.loadToken) self.config.onMetadata(product);
      }).catch(function () { /* loadProduct reports preparation errors below. */ });
    }
    return preparedProduct
      .then(function (prepared) {
        if (token !== self.loadToken || !prepared) return null;
        self.activeSku = normalizedSku;
        self.product = prepared.product;
        self.backPanel = prepared.backPanel;
        self.frontLayer = prepared.frontLayer;
        self.frontSource = prepared.frontSource || null;
        self.backSource = prepared.backSource || null;
        self.digits = prepared.digits || null;
        self.numberFrontLayers = prepared.numberFrontLayers || null;
        self.trimProductCache();
        return prepared.product;
      })
      .catch(function (error) {
        if (token === self.loadToken) {
          self.config.onStatus('error', error.message);
        }
        throw error;
      });
  };

  SoloCustomJerseyPreview.prototype.render = function (values) {
    var self = this;
    if (!this.product || (!this.frontLayer && !this.frontSource) || !this.backPanel) {
      return Promise.resolve(null);
    }

    return this.loadResources().then(function (resources) {
      var panel = makeCanvas(self.backPanel.width, self.backPanel.height);
      panel.getContext('2d').drawImage(self.backPanel, 0, 0);
      drawCustomization(
        panel,
        self.product.customization,
        values,
        typeof self.product.customization.nameTopInset === 'number' ? self.product.customization.nameTopInset : self.config.nameTopInset
      );
      var frontLayer = self.frontLayer;
      if (self.frontSource) {
        var numberKey = String(values.number || '');
        frontLayer = self.numberFrontLayers.get(numberKey);
        if (!frontLayer) {
          var front = personalizeNflFront(self.frontSource, self.product.customization, numberKey, self.digits);
          frontLayer = resources.psdMock
            ? global.RockEmNflJerseyPsdMock.front(front.source, resources.psdMock)
            : createFrontLayer(makeTile(front.panel), resources);
          self.numberFrontLayers.set(numberKey, frontLayer);
          while (self.numberFrontLayers.size > 3) {
            self.numberFrontLayers.delete(self.numberFrontLayers.keys().next().value);
          }
        }
      }
      var mock;
      if (resources.psdMock) {
        var back = makeCanvas(self.backSource.width, self.backSource.height);
        var backContext = back.getContext('2d');
        backContext.drawImage(self.backSource, 0, 0);
        var area = self.product.customization.printBackArea;
        backContext.drawImage(panel, area.x, area.y);
        mock = global.RockEmNflJerseyPsdMock.render(frontLayer, back, resources.psdMock);
      } else {
        mock = renderMock(frontLayer, makeTile(panel), resources);
      }
      self.config.canvases.forEach(function (canvas) {
        copyCanvas(mock, canvas);
      });
      self.config.onStatus('ready', '');
      if (typeof self.config.onRender === 'function') {
        try {
          self.config.onRender(mock, values);
        } catch (onRenderError) {
          // A failure in the caller's hook (e.g. uploading the mockup) must
          // never be mistaken for a render failure.
        }
      }
      return mock;
    }).catch(function (error) {
      self.config.onStatus('error', error.message);
      throw error;
    });
  };

  SoloCustomJerseyPreview.prototype.selectNumberSet = function (id) {
    var self = this;
    if (!this.product || this.product.renderer.id !== 'nfl-custom-jersey-2026') {
      return Promise.reject(new Error('Load an NFL jersey design first.'));
    }
    var sku = this.activeSku;
    var token = ++this.loadToken;
    var url = this.config.endpoint +
      (this.config.endpoint.indexOf('?') === -1 ? '?' : '&') +
      'sku=' + encodeURIComponent(sku) + '&numberSet=' + encodeURIComponent(id);
    return Promise.all([fetchJson(url), this.config.mockProfile ? this.loadResources() : Promise.resolve({})]).then(function (loaded) {
      var product = applyNflPlacements(loaded[0]);
      return Promise.all([
        loaded[1].psdMock ? global.RockEmNflJerseyPsdMock.loadSource(resolveUrl(product.assets.front.url, url)) : loadImage(resolveUrl(product.assets.front.url, url)),
        loaded[1].psdMock ? global.RockEmNflJerseyPsdMock.loadSource(resolveUrl(product.assets.back.url, url)) : loadImage(resolveUrl(product.assets.back.url, url)),
        loadNumberSetImages(product.customization.numberSet, url)
      ]).then(function (assets) {
        if (token !== self.loadToken || sku !== self.activeSku) return null;
        var backPanel = cropPanel(assets[1], getSoloPanelArea(assets[1], product.customization.printBackArea));
        self.product = product;
        self.frontSource = assets[0];
        self.backSource = assets[1];
        self.backPanel = backPanel;
        self.digits = assets[2];
        self.numberFrontLayers = new Map();
        return product;
      });
    });
  };

  // Pilot tooling can export the same personalized panels used by the mock.
  // These are doubled print tiles; production routing is enabled separately.
  SoloCustomJerseyPreview.prototype.createPrintTiles = function (values, options) {
    var self = this;
    if (!this.product || !this.frontSource || !this.backPanel) {
      return Promise.reject(new Error('Load an NFL jersey design first.'));
    }
    if (String(values.name || '').length > 12 || !/^\d{0,2}$/.test(String(values.number || ''))) {
      return Promise.reject(new Error('Use up to 12 name characters and two number digits.'));
    }
    var source = options && options.sku && options.sku !== this.activeSku
      ? this.prepareProduct(options.sku)
      : Promise.resolve({ product: this.product, frontSource: this.frontSource, backPanel: this.backPanel, digits: this.digits });
    return Promise.all([this.loadResources(), source]).then(function (loaded) {
      var resources = loaded[0], prepared = loaded[1], product = prepared.product;
      if (values._numberSet && product.customization.numberSet.id !== values._numberSet) throw new Error('The size artwork belongs to a different number style.');
      var front = personalizeNflFront(prepared.frontSource, product.customization, values.number, prepared.digits);
      var back = makeCanvas(prepared.backPanel.width, prepared.backPanel.height);
      back.getContext('2d').drawImage(prepared.backPanel, 0, 0);
      drawCustomization(back, product.customization, values, typeof product.customization.nameTopInset === 'number' ? product.customization.nameTopInset : self.config.nameTopInset);
      if (resources.psdMock) {
        global.RockEmNflJerseyPsdMock.toSrgb(front.panel, resources.psdMock);
        global.RockEmNflJerseyPsdMock.toSrgb(back, resources.psdMock);
      }
      return {
        front: makeTile(front.panel),
        back: makeTile(back),
        selectionVersion: product.customization.designSelection && product.customization.designSelection.selectionVersion
      };
    });
  };

  global.RockEmSoloCustomJerseyPreview = SoloCustomJerseyPreview;
})(window);
